import React, { ReactNode } from 'react'
import { VStack, HStack, Box, Text, Link, Spinner } from '@chakra-ui/react'
import { ITheme } from '../../../../interfaces/theme'
import EmptyResult from '../EmptyResult'

interface DashboardSectionCardProps {
  icon: ReactNode
  title: string
  viewMoreLink?: {
    label: string
    onClick: () => void
  }
  isLoading?: boolean
  isEmpty?: boolean
  emptyTitle?: string
  emptyDescription?: string
  children: ReactNode
  scrollContainerId?: string
  currentTheme?: ITheme
  loadingComponent?: ReactNode
}

const DashboardSectionCard = ({
  icon,
  title,
  viewMoreLink,
  isLoading = false,
  isEmpty = false,
  emptyTitle,
  emptyDescription,
  children,
  scrollContainerId,
  currentTheme,
  loadingComponent
}: DashboardSectionCardProps) => {
  const scrollbarStyles = {
    '&::-webkit-scrollbar': {
      width: '8px'
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: '4px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '4px'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555'
    }
  }

  return (
    <VStack bg="#FFFFFF" borderRadius="8px" gap={4} width="full" alignSelf="flex-start">
      <VStack padding={4} spacing={4} alignItems="flex-start" alignSelf="flex-start" width="full">
        <HStack spacing={4} minWidth="max-content" justifyContent="space-between" width="100%">
          <HStack spacing={2} alignItems="center" alignSelf="flex-start">
            {icon}
            <Text fontSize="18px" color="gray.800" fontWeight="600" lineHeight="28px" marginBottom={0}>
              {title}
            </Text>
          </HStack>
          {viewMoreLink && (
            <Box onClick={viewMoreLink.onClick}>
              <Link
                color={currentTheme?.primaryColor ?? 'primary.500'}
                fontSize="16px"
                fontWeight="500"
                lineHeight="24px"
                _hover={{
                  color: currentTheme?.primaryColor ?? 'primary.700',
                  opacity: currentTheme?.primaryColor ? 0.8 : 1
                }}
              >
                {viewMoreLink.label}
              </Link>
            </Box>
          )}
        </HStack>

        {isLoading ? (
          <Box
            width="full"
            height="500px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            marginBottom="16px"
          >
            {loadingComponent || <Spinner size="md" color="blue.500" />}
          </Box>
        ) : isEmpty && emptyTitle && emptyDescription ? (
          <Box
            width="full"
            height="500px"
            overflowY="auto"
            marginBottom="16px"
            css={scrollbarStyles}
          >
            <EmptyResult title={emptyTitle} description={emptyDescription} />
          </Box>
        ) : (
          <Box
            id={scrollContainerId}
            width="full"
            height="500px"
            overflowY="auto"
            marginBottom="16px"
            css={scrollbarStyles}
          >
            {children}
          </Box>
        )}
      </VStack>
    </VStack>
  )
}

export default DashboardSectionCard

